/**
 * Purchase Order QR → PDF
 *
 * Fetches QR data from BE (`GET /api/purchase-orders/:id/qr-data`),
 * renders the QR client-side via `qrcode`, lays it out on an A4 sheet
 * via `jspdf`, and triggers a browser download.
 *
 * Dependencies (admin-portal package.json):
 *   - "qrcode" + "@types/qrcode"
 *   - "jspdf"
 *
 * Used by the PO row dropdown menu in IncomingMaterialsSection.
 */

import { fetchPurchaseOrderQrData } from '../api/inbound-shipments-api'

const QR_PIXEL_SIZE = 600
const PAGE_QR_MM = 100
const PAGE_QR_OFFSET_X_MM = 55 // (210 - 100) / 2

interface QrPayloadShape {
  type?: string
  purchaseOrderNumber?: string | null
  sapPurchaseOrderNumber?: string | null
}

function todayLabel(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Build the printable PDF blob in memory.
 * Exported separately so callers can preview without forcing a download.
 */
export async function buildPurchaseOrderQrPdfBlob(
  purchaseOrderId: number,
): Promise<{ blob: Blob; filename: string; poNumber: string }> {
  const [qrData, qrcodeMod, jsPdfMod] = await Promise.all([
    fetchPurchaseOrderQrData(purchaseOrderId),
    import('qrcode'),
    import('jspdf'),
  ])

  const QRCode = (qrcodeMod as any).default ?? qrcodeMod
  const JsPdfCtor = (jsPdfMod as any).jsPDF ?? (jsPdfMod as any).default

  const dataUrl: string = await QRCode.toDataURL(qrData.qrText, {
    width: QR_PIXEL_SIZE,
    margin: 1,
    errorCorrectionLevel: 'M',
  })

  const pdf = new JsPdfCtor({ unit: 'mm', format: 'a4' })
  const payload = (qrData.qrPayload || {}) as QrPayloadShape
  const poNumber = qrData.purchaseOrder.poNumber

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(20)
  pdf.text('Purchase Order QR', 105, 25, { align: 'center' })

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(13)
  pdf.text(`PO Number: ${poNumber}`, 105, 38, { align: 'center' })

  pdf.addImage(dataUrl, 'PNG', PAGE_QR_OFFSET_X_MM, 55, PAGE_QR_MM, PAGE_QR_MM)

  pdf.setFontSize(11)
  pdf.text('Scan at the inbound gate to start receipt', 105, 170, { align: 'center' })

  pdf.setFontSize(9)
  pdf.setTextColor(120)
  pdf.text(`Generated ${todayLabel()}`, 105, 285, { align: 'center' })

  const blob = pdf.output('blob') as Blob
  const filename = `PO-${poNumber}-QR.pdf`
  return { blob, filename, poNumber }
}

/** Build the PDF and trigger a browser download. */
export async function downloadPurchaseOrderQrPdf(purchaseOrderId: number): Promise<void> {
  const { blob, filename } = await buildPurchaseOrderQrPdfBlob(purchaseOrderId)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
